import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Typography, Box, TextField,
  alpha, InputAdornment, IconButton,
} from '@mui/material';
import {
  Save as SaveIcon, Handshake as PartnerIcon, Business as BusinessIcon,
  AttachMoney as SponsorIcon, Description as DescriptionIcon,
  Flag as FlagIcon, Category as CategoryIcon,
  CalendarToday as CalendarTodayIcon,
  PictureAsPdf as PdfIcon,
  CloudUpload as UploadIcon, Delete as DeleteIcon,
  SwapHoriz as ReplaceIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import SearchableSelect from '../../common/SearchableSelect';
import { inputRootSx, makePickerSlots } from '../../common/formFieldSx';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildPdfEmbedSrc, fetchPartnerDocumentPreviewBlob, openPartnerDocumentInNewTab, openBlobUrlInNewTab } from '../../../utils/protectedFilePreview';
import {
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
  applyApiValidationErrors,
} from '../../../utils/formValidationFeedback';

const PARTNER_VALIDATION_FIELDS = new Set([
  'company_id', 'sponsor_tier_id', 'sponsor_amount',
  'sponsor_start_date', 'sponsor_end_date',
]);

const collectPartnerValidationErrors = (form) => {
  const errors = {};
  if (!form.company_id) errors.company_id = 'Please select a company.';
  if (!form.sponsor_tier_id) errors.sponsor_tier_id = 'Please select a sponsor tier.';
  const rawAmount = String(form.sponsor_amount ?? '').trim();
  const amount = Number.parseFloat(rawAmount);
  if (rawAmount === '') errors.sponsor_amount = 'Sponsor amount is required.';
  else if (Number.isNaN(amount) || amount < 0) {
    errors.sponsor_amount = 'Sponsor amount must be a number greater than or equal to 0.';
  }
  const start = form.sponsor_start_date != null ? dayjs(form.sponsor_start_date) : null;
  const end = form.sponsor_end_date != null ? dayjs(form.sponsor_end_date) : null;
  if (!start?.isValid?.() || !end?.isValid?.()) {
    errors.sponsor_end_date = 'Please choose valid sponsor dates.';
  } else if (end.isBefore(start, 'day')) {
    errors.sponsor_end_date = 'Sponsor end date must be on or after the start date.';
  }
  return errors;
};

const B = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;
const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiFormLabel-asterisk { color: #EF4444 !important; }
  .MuiInputLabel-root.Mui-required::after { color: #EF4444 !important; }
`;

const PICKER_CSS = `
  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.22) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.65) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.65rem !important;
    font-weight: 600 !important;
    letter-spacing: 1px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.18) !important;
    color: #38BDF8 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.3) !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    border: 2px solid #38BDF8 !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.6) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid rgba(56,189,248,0.65) !important;
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.08) !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.75) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiYearCalendar-root button,
  .MuiMonthCalendar-root button {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiYearCalendar-root button:hover,
  .MuiMonthCalendar-root button:hover {
    background: rgba(56,189,248,0.1) !important;
    color: #38BDF8 !important;
  }
  .MuiYearCalendar-root button.Mui-selected,
  .MuiMonthCalendar-root button.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.5) !important;
    border-radius: 8px !important;
  }
`;

const TEXT_BODY = '#E2E8F0';

const monoSx = (color = alpha(NEON, 0.55), size = '0.58rem') => ({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: size,
  fontWeight: 600,
  letterSpacing: 1.8,
  textTransform: 'uppercase',
  color,
  lineHeight: 1,
});

const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active'   },
  { value: 'inactive', label: 'Archived / Inactive' },
  { value: 'pending',  label: 'Pending'  },
];
const PUBLICITY_OPTIONS = [
  { value: 'public',  label: 'Public'  },
  { value: 'private', label: 'Private' },
];

const PARTNER_DOCUMENTS = [
  { key: 'signed_agreement', pathKey: 'signed_agreement_path', label: 'Signed agreement'       },
  { key: 'receipt_utm',      pathKey: 'receipt_utm_path',      label: 'Receipt (UTM)'          },
  { key: 'receipt_company',  pathKey: 'receipt_company_path',  label: 'Receipt (company)'      },
  { key: 'invoice',          pathKey: 'invoice_path',          label: 'Invoice'                },
  { key: 'other_document',   pathKey: 'other_document_path',   label: 'Other related document' },
];

const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

const formatBytes = (bytes = 0) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const isPdfPath = (path = '') => String(path).toLowerCase().endsWith('.pdf');

const PartnerDocumentUploadZone = ({
  label, selectedFile, existingFilePath, hasExisting,
  partnerId, docKey,
  onFileDrop, onReplace, onRemove,
}) => {
  const [isDragOver,      setIsDragOver]      = useState(false);
  const [previewOpen,     setPreviewOpen]     = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [previewIsPdf,    setPreviewIsPdf]    = useState(false);
  const [previewLoading,  setPreviewLoading]  = useState(false);
  const [previewError,    setPreviewError]    = useState('');

  const hasFile = Boolean(selectedFile || hasExisting);
  const isNewPdf = Boolean(
    selectedFile && (selectedFile.type === 'application/pdf' || isPdfPath(selectedFile.name)),
  );
  const canExpandPreview = isNewPdf || Boolean(hasExisting && existingFilePath);
  const canOpenInTab = Boolean(documentBlobUrl || selectedFile || (hasExisting && existingFilePath));

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;

    const cleanup = () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };

    setPreviewError('');
    setPreviewLoading(false);

    if (selectedFile) {
      const objUrl = URL.createObjectURL(selectedFile);
      revokeUrl = objUrl;
      const pdf = selectedFile.type === 'application/pdf' || isPdfPath(selectedFile.name);
      setDocumentBlobUrl(objUrl);
      setPreviewIsPdf(pdf);
      return cleanup;
    }

    setDocumentBlobUrl(null);
    setPreviewIsPdf(false);

    if (!existingFilePath) {
      return undefined;
    }

    setPreviewLoading(true);
    fetchPartnerDocumentPreviewBlob(partnerId, docKey, existingFilePath)
      .then(({ url, isPdf }) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        revokeUrl = url;
        setDocumentBlobUrl(url);
        setPreviewIsPdf(isPdf);
        setPreviewError(isPdf ? '' : 'Inline preview is only available for PDF files.');
      })
      .catch((err) => {
        if (!cancelled) {
          setDocumentBlobUrl(null);
          setPreviewIsPdf(false);
          setPreviewError(err.message || 'Preview unavailable — use Open in new tab or re-upload the file.');
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [selectedFile, existingFilePath, partnerId, docKey]);

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragOver(true);  };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop      = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(false);
    onFileDrop?.(e.dataTransfer?.files?.[0]);
  };

  const handleOpenInNewTab = async () => {
    try {
      if (documentBlobUrl) {
        openBlobUrlInNewTab(documentBlobUrl);
        setPreviewError('');
        return;
      }
      if (!existingFilePath && !partnerId) return;
      await openPartnerDocumentInNewTab(partnerId, docKey, existingFilePath);
      setPreviewError('');
    } catch (err) {
      setPreviewError(err.message || 'Could not open document — try again or re-upload the file.');
    }
  };

  if (!hasFile) {
    return (
      <Box
        onClick={onReplace}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 3, borderRadius: '12px', cursor: 'pointer',
          border: `2px dashed ${isDragOver ? NEON : alpha(NEON, 0.28)}`,
          background: isDragOver ? alpha(NEON, 0.08) : alpha(SURFACE_UP, 0.4),
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: alpha(NEON, 0.6),
            background: alpha(NEON, 0.04),
            boxShadow: `0 0 20px ${alpha(NEON, 0.15)}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <UploadIcon sx={{ fontSize: 30, color: isDragOver ? NEON : alpha(NEON, 0.55), mb: 1 }} />
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: alpha(TEXT_BODY, 0.8), textAlign: 'center' }}>
            Upload {label}
          </Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: alpha(NEON, 0.45), mt: 0.5 }}>
            Click to browse or drag & drop
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'relative', borderRadius: '12px',
      border: `1px solid ${alpha(NEON, 0.24)}`,
      background: `linear-gradient(135deg, ${alpha(NEON, 0.06)} 0%, ${alpha(SURFACE_UP, 0.65)} 100%)`,
      p: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: alpha('#F87171', 0.15),
          border: `1px solid ${alpha('#F87171', 0.3)}`,
          flexShrink: 0,
        }}>
          <PdfIcon sx={{ fontSize: 20, color: '#F87171' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', color: alpha(TEXT_BODY, 0.9), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedFile?.name || `${label} (existing file)`}
          </Typography>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(NEON, 0.45), mt: 0.3 }}>
            {selectedFile ? formatBytes(selectedFile.size) : 'Saved on server'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
          {canExpandPreview && (
            <IconButton
              onClick={() => setPreviewOpen(v => !v)} size="small"
              sx={{ background: alpha(NEON2, 0.15), color: NEON2, '&:hover': { background: alpha(NEON2, 0.25) } }}
            >
              {previewOpen
                ? <ExpandLessIcon sx={{ fontSize: 16 }} />
                : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          )}
          {canOpenInTab && (
            <IconButton
              onClick={handleOpenInNewTab}
              size="small"
              sx={{ background: alpha(NEON, 0.12), color: NEON, '&:hover': { background: alpha(NEON, 0.22) } }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <IconButton
            onClick={onReplace} size="small"
            sx={{ background: alpha(NEON, 0.15), color: NEON, '&:hover': { background: alpha(NEON, 0.25) } }}
          >
            <ReplaceIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            onClick={onRemove} size="small"
            sx={{ background: alpha('#EF4444', 0.15), color: '#F87171', '&:hover': { background: alpha('#EF4444', 0.25) } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {canExpandPreview && previewOpen && (
        <Box sx={{ mt: 1.5, borderTop: `1px solid ${alpha(NEON, 0.15)}`, pt: 1.25 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(NEON, 0.5), letterSpacing: 0.5 }}>
              {previewIsPdf ? 'PDF PREVIEW' : 'DOCUMENT PREVIEW'}
            </Typography>
            <IconButton
              onClick={handleOpenInNewTab}
              size="small"
              disabled={!canOpenInTab}
              sx={{ color: NEON, background: alpha(NEON, 0.1), '&:hover': { background: alpha(NEON, 0.2) } }}
            >
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
          <Box sx={{ height: 360, borderRadius: '10px', overflow: 'hidden', border: `1px solid ${alpha(NEON, 0.18)}` }}>
            {previewLoading ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(TEXT_BODY, 0.7), textAlign: 'center' }}>
                  Loading preview…
                </Typography>
              </Box>
            ) : previewIsPdf && documentBlobUrl ? (
              <iframe
                src={buildPdfEmbedSrc(documentBlobUrl)}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                title={`${label} preview`}
              />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(TEXT_BODY, 0.7), textAlign: 'center' }}>
                  {previewError || 'Preview unavailable — use Open in new tab.'}
                </Typography>
              </Box>
            )}
          </Box>
          {previewError && (
            <Typography sx={{ mt: 0.75, fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: '#F87171' }}>
              {previewError}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const buildDatePickerSlotProps = (label, { required, helperText, error, setOpen, disabled = false } = {}) => {
  const slots = makePickerSlots('normal', <CalendarTodayIcon />)(disabled);
  const openPicker = () => { if (!disabled) setOpen(true); };
  return {
    ...slots,
    textField: {
      ...slots.textField,
      required,
      label,
      helperText,
      error: Boolean(error),
      onClick: openPicker,
      onKeyDown: (e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPicker(); }
      },
    },
    openPickerButton: {
      onClick: (e) => { e.stopPropagation(); openPicker(); },
    },
  };
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const PartnerForm = () => {
  const navigate = useNavigate();
  const { id }   = useParams();
  const { user: currentUser } = useAuth();

  const isEdit = Boolean(id);

  const [loading,      setLoading]      = useState(false);
  const [dataLoading,  setDataLoading]  = useState(isEdit);
  const [error,        setError]        = useState(null);
  const [success,      setSuccess]      = useState(null);
  const [confirmOpen,  setConfirmOpen]  = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [companies,    setCompanies]    = useState([]);
  const [tiers,        setTiers]        = useState([]);
  const [documentFiles,        setDocumentFiles]        = useState({});
  const [documentPreviews,     setDocumentPreviews]     = useState({});
  const [existingDocuments,    setExistingDocuments]    = useState({});
  const [existingDocumentPaths,setExistingDocumentPaths]= useState({});
  const [removedDocuments,     setRemovedDocuments]     = useState({});
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen,   setEndPickerOpen]   = useState(false);
  const feedbackRef = useRef(null);

  const [formData, setFormData] = useState({
    company_id:        '',
    sponsor_tier_id:   '',
    sponsor_amount:    '',
    status:            'active',
    publicity:         'public',
    notes:             '',
    sponsor_start_date: dayjs(),
    sponsor_end_date:   dayjs().add(1, 'year'),
  });

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'partner',
    userId: currentUser?.id,
    isEditMode: isEdit,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  useEffect(() => {
    const cssId = 'partnerform-ds-css';
    let el = document.getElementById(cssId);
    if (!el) {
      el = document.createElement('style');
      el.id = cssId;
      document.head.appendChild(el);
    }
    el.innerHTML = FORM_CSS + PICKER_CSS;
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    (async () => {
      try {
        const [cRes, tRes] = await Promise.all([
          api.get('/companies'),
          api.get('/sponsor-tiers'),
        ]);
        setCompanies(Array.isArray(cRes.data) ? cRes.data : []);
        const tList = Array.isArray(tRes.data) ? tRes.data : [];
        setTiers(tList.sort((a, b) => Number(a.sponsor_amount) - Number(b.sponsor_amount)));
      } catch {
        setCompanies([]); setTiers([]);
      }
    })();
    if (isEdit && id) fetchPartner();
    else setDataLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, id, isEdit]);

  useEffect(() => {
    if (isEdit || !tiers.length) return;
    setFormData(prev =>
      prev.sponsor_tier_id ? prev : { ...prev, sponsor_tier_id: String(tiers[0].id) }
    );
  }, [tiers, isEdit]);

  useEffect(() => {
    if (!error && !success) return;
    window.requestAnimationFrame(() => {
      feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [error, success]);

  const companySelectOptions = useMemo(
    () => companies.map(c => ({ value: String(c.id), label: c.name })),
    [companies]
  );

  const tierSelectOptions = useMemo(
    () => tiers.map(t => ({
      value: String(t.id),
      label: `${t.tier_name} — RM ${Number(t.sponsor_amount).toLocaleString()}`,
    })),
    [tiers]
  );

  const fetchPartner = async () => {
    try {
      setDataLoading(true);
      const response = await api.get(`/partners/${id}`);
      const p = response.data;
      setFormData({
        company_id:        p.company_id       != null ? String(p.company_id)       : '',
        sponsor_tier_id:   p.sponsor_tier_id  != null ? String(p.sponsor_tier_id)  : '',
        sponsor_amount:    p.sponsor_amount   != null ? String(p.sponsor_amount)   : '',
        status:            p.status    || 'active',
        publicity:         p.publicity || 'public',
        notes:             p.notes     || '',
        sponsor_start_date: p.sponsor_start_date ? dayjs(p.sponsor_start_date) : dayjs(),
        sponsor_end_date:   p.sponsor_end_date   ? dayjs(p.sponsor_end_date)   : dayjs().add(1, 'year'),
      });
      const nextExisting      = {};
      const nextExistingPaths = {};
      PARTNER_DOCUMENTS.forEach(({ key, pathKey }) => {
        nextExisting[key]      = Boolean(p[pathKey]);
        nextExistingPaths[key] = p[pathKey] || '';
      });
      setExistingDocuments(nextExisting);
      setExistingDocumentPaths(nextExistingPaths);
      setDocumentFiles({});
      setDocumentPreviews({});
      setRemovedDocuments({});
      setValidationErrors({});
    } catch {
      setError('Failed to load partner details.');
      navigate('/partners');
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (showFieldErrors && field === 'sponsor_start_date' && validationErrors.sponsor_end_date) {
      setValidationErrors((prev) => ({ ...prev, sponsor_end_date: '' }));
    }
  };

  const handleNativeChange = (e) => handleChange(e.target.name, e.target.value);

  const processDocumentFile = (docKey, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Document must be less than 10MB'); return; }
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg', 'image/png', 'image/gif',
    ];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid document (PDF, Word, or image)');
      return;
    }
    setDocumentFiles(prev    => ({ ...prev,    [docKey]: file      }));
    setDocumentPreviews(prev => ({ ...prev,    [docKey]: file.name }));
    setRemovedDocuments(prev => { const n = { ...prev }; delete n[docKey]; return n; });
    setError(null);
  };

  const handleDocumentChange = (docKey, e) => processDocumentFile(docKey, e.target.files?.[0]);

  const removeDocument = (docKey) => {
    const hadExisting = Boolean(existingDocuments[docKey] || existingDocumentPaths[docKey]);
    setDocumentFiles(prev        => { const n = { ...prev }; delete n[docKey]; return n; });
    setDocumentPreviews(prev     => { const n = { ...prev }; delete n[docKey]; return n; });
    setExistingDocumentPaths(prev => ({ ...prev, [docKey]: ''    }));
    setExistingDocuments(prev    => ({ ...prev, [docKey]: false  }));
    setRemovedDocuments(prev => (
      isEdit && hadExisting
        ? { ...prev, [docKey]: true }
        : (() => { const n = { ...prev }; delete n[docKey]; return n; })()
    ));
  };

  const hasUploadedFile = (key) => documentFiles[key] instanceof File;

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('company_id',      formData.company_id);
    fd.append('sponsor_tier_id', formData.sponsor_tier_id);
    fd.append('sponsor_amount',  String(Number.parseFloat(formData.sponsor_amount)));
    fd.append('status',          formData.status);
    fd.append('publicity',       formData.publicity);
    const notes = formData.notes?.trim();
    if (notes) fd.append('notes', notes);
    fd.append('sponsor_start_date', dayjs(formData.sponsor_start_date).format('YYYY-MM-DD'));
    fd.append('sponsor_end_date',   dayjs(formData.sponsor_end_date).format('YYYY-MM-DD'));
    PARTNER_DOCUMENTS.forEach(({ key }) => {
      if (hasUploadedFile(key)) fd.append(key, documentFiles[key]);
      else if (removedDocuments[key]) fd.append(`remove_${key}`, '1');
    });
    return fd;
  };

  const validateForm = () => {
    const errors = collectPartnerValidationErrors(formData);
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setConfirmOpen(true);
      return;
    }
    if (!validateForm()) return;
    doSave();
  };

  const handleConfirmUpdate = () => {
    const errors = collectPartnerValidationErrors(formData);
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen: setConfirmOpen,
        setShowFieldErrors,
      });
      return;
    }
    setConfirmOpen(false);
    doSave();
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      const fd = buildFormData();
      if (isEdit) {
        fd.append('_method', 'PUT');
        await api.post(`/partners/${id}`, fd);
      } else {
        await api.post('/partners', fd);
      }
      if (!isEdit) formDraft.clearDraft();
      setSuccess(`Partner ${isEdit ? 'updated' : 'created'} successfully!`);
      setTimeout(() => navigate('/partners', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      const data = err.response?.data;
      applyApiValidationErrors(data, setValidationErrors, PARTNER_VALIDATION_FIELDS);
      const fieldMsgs = {};
      const errs = data?.errors;
      if (errs && typeof errs === 'object') {
        Object.entries(errs).forEach(([key, msgs]) => {
          if (!PARTNER_VALIDATION_FIELDS.has(key)) return;
          const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
          if (msg) fieldMsgs[key] = String(msg);
        });
      }
      if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
      setError(
        Object.keys(fieldMsgs).length
          ? formatFieldErrorsMessage(fieldMsgs)
          : (getFirstErrorMessage(data) || `Failed to ${isEdit ? 'update' : 'create'} partner.`),
      );
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) return null;
  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading partner form" size={56} variant="primary" />
    </Box>
  );

  const canSubmit = Boolean(
    formData.company_id
    && formData.sponsor_tier_id
    && String(formData.sponsor_amount ?? '').trim()
    && formData.sponsor_start_date
    && formData.sponsor_end_date,
  );
  const submitDisabled = loading || !canSubmit;
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  const companyLabel = companies.find(c => String(c.id) === String(formData.company_id))?.name || formData.company_id;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
        <Header
          title={isEdit ? 'Edit Partner' : 'Create New Partner'}
          subtitle={isEdit ? 'Update sponsorship record' : 'Link a company as a partner or sponsor'}
          icon={HandshakeIcon}
          showBackButton
          onBackClick={() => navigate(-1)}
        />

        <Box ref={feedbackRef} sx={{ scrollMarginTop: { xs: 96, md: 112 } }}>
          <FeedbackMessage message={error}   severity="error"   onClose={() => setError(null)}   />
          <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)} />
        </Box>

        {formDraft.showNotice && (
          <FormDraftNotice
            savedAt={formDraft.restoredAt}
            onDiscard={formDraft.discardDraft}
            onDismiss={formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Partnership details ── */}
            <CommonSectionCard title="Partnership" icon={<BusinessIcon />} delay={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <SearchableSelect
                  required label="Company"
                  value={formData.company_id}
                  onChange={v => handleChange('company_id', v)}
                  options={companySelectOptions}
                  error={fieldHasError('company_id')}
                  helperText={fieldHelper('company_id')}
                  startIcon={<BusinessIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <SearchableSelect
                    required label="Sponsor tier"
                    value={formData.sponsor_tier_id}
                    onChange={v => handleChange('sponsor_tier_id', v)}
                    options={tierSelectOptions}
                    error={fieldHasError('sponsor_tier_id')}
                    helperText={fieldHelper('sponsor_tier_id')}
                    startIcon={<CategoryIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  />
                  <StyledField
                    fullWidth required label="Sponsor amount (MYR)"
                    name="sponsor_amount" type="number"
                    value={formData.sponsor_amount}
                    onChange={handleNativeChange}
                    error={fieldHasError('sponsor_amount')}
                    helperText={fieldHelper('sponsor_amount')}
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SponsorIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <SearchableSelect
                    required label="Status"
                    value={formData.status}
                    onChange={v => handleChange('status', v)}
                    options={STATUS_OPTIONS}
                    startIcon={<FlagIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                  <SearchableSelect
                    required label="Publicity"
                    value={formData.publicity}
                    onChange={v => handleChange('publicity', v)}
                    options={PUBLICITY_OPTIONS}
                    startIcon={<FlagIcon sx={{ color: alpha(NEON2, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            </CommonSectionCard>

            {/* ── Sponsor period ── */}
            <CommonSectionCard title="Sponsor period" icon={<CalendarTodayIcon />} delay={0.08}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                <DatePicker
                  label="Sponsor start date"
                  value={formData.sponsor_start_date}
                  open={startPickerOpen}
                  onOpen={() => setStartPickerOpen(true)}
                  onClose={() => setStartPickerOpen(false)}
                  onChange={v => handleChange('sponsor_start_date', v)}
                  slotProps={buildDatePickerSlotProps('Sponsor start date', {
                    required: true,
                    setOpen: setStartPickerOpen,
                  })}
                  sx={{ flex: 1 }}
                />
                <DatePicker
                  label="Sponsor end date"
                  value={formData.sponsor_end_date}
                  open={endPickerOpen}
                  onOpen={() => setEndPickerOpen(true)}
                  onClose={() => setEndPickerOpen(false)}
                  onChange={v => handleChange('sponsor_end_date', v)}
                  slotProps={buildDatePickerSlotProps('Sponsor end date', {
                    required: true,
                    error: fieldHasError('sponsor_end_date'),
                    helperText: fieldHelper('sponsor_end_date'),
                    setOpen: setEndPickerOpen,
                  })}
                  sx={{ flex: 1 }}
                />
              </Box>
            </CommonSectionCard>

            {/* ── Notes & documents ── */}
            <CommonSectionCard title="Notes & document" icon={<DescriptionIcon />} delay={0.12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <StyledField
                  fullWidth label="Notes" name="notes"
                  value={formData.notes} onChange={handleNativeChange}
                  multiline rows={3}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: '14px' }}>
                        <DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box>
                  <Typography sx={{ ...monoSx(alpha(NEON, 0.65), '0.62rem'), mb: 1.5 }}>
                    Partner documents
                  </Typography>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.65), mb: 1.5 }}>
                    PDF, Word, or images. Leave blank if you will attach files later.
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {PARTNER_DOCUMENTS.map(({ key, label }) => {
                      const inputId    = `partner-document-${key}`;
                      const file       = hasUploadedFile(key) ? documentFiles[key] : null;
                      const preview    = documentPreviews[key];
                      const hasExisting = Boolean(existingDocuments[key]);

                      return (
                        <Box key={key}>
                          <input
                            type="file"
                            accept="application/pdf,image/*,.doc,.docx"
                            onChange={e => handleDocumentChange(key, e)}
                            style={{ display: 'none' }}
                            id={inputId}
                          />
                          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', color: alpha(NEON, 0.55), mb: 0.75 }}>
                            {label}
                          </Typography>
                          <PartnerDocumentUploadZone
                            label={label}
                            partnerId={id}
                            docKey={key}
                            selectedFile={preview ? file : null}
                            existingFilePath={existingDocumentPaths[key]}
                            hasExisting={isEdit && hasExisting && !file}
                            onFileDrop={droppedFile => processDocumentFile(key, droppedFile)}
                            onReplace={() => document.getElementById(inputId)?.click()}
                            onRemove={() => removeDocument(key)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </CommonSectionCard>

            {/* ── Submit ── */}
            <SubmitActionsCard
              statusText={isEdit ? 'Save changes to update this partner.' : 'Complete the form to add a new partner.'}
              missingRequiredFields={isEdit ? null : [!formData.company_id && 'Company', !formData.sponsor_tier_id && 'Sponsor Tier', !String(formData.sponsor_amount ?? '').trim() && 'Sponsor Amount', !formData.sponsor_start_date && 'Start Date', !formData.sponsor_end_date && 'End Date'].filter(Boolean)}
              readyStatusText="All required fields are complete. Ready to create partner."
              cancelLabel="Cancel"
              onCancel={() => navigate(-1)}
              cancelDisabled={loading}
              submitLabel={isEdit ? 'Update Partner' : 'Create Partner'}
              submitDisabled={submitDisabled}
              loading={loading}
              submitStartIcon={<SaveIcon />}
              showSubmit
              submitType="submit"
              {...submitDraftProps}
            />

          </Box>
        </form>

        <ConfirmDeleteDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmUpdate}
          loading={loading}
          variant="edit"
          title="Confirm Update"
          itemType="partner"
          itemName={companyLabel || 'this partner'}
          description="Please review your changes before saving. This will overwrite the current partner record."
          confirmLabel="Update Partner"
          confirmColor="primary"
          confirmDisabled={!canSubmit}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default PartnerForm;
